import axios from 'axios';
import { getConfig } from '../config';
import type { GlowpickProduct, GlowpickSearchResponse } from '../models/Glowpick';

interface MetadataResponse {
  metadata?: {
    brands?: string[];
    categories?: string[];
    subcategories?: Record<string, string[]>;
    lastProductId?: number;
    totalProducts?: number;
  };
}

interface LastProductIdResponse {
  productId: string;
  metadata: {
    lastProductId: number;
  };
  createdAt: string;
  updatedAt: string;
}

export class GlowpickClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getConfig().apiUrl;
  }

  // Get products with optional pagination - now returns pagination info
  async getProducts(limit?: number, lastEvaluatedKey?: string): Promise<GlowpickSearchResponse> {
    const params = new URLSearchParams();

    if (limit) {
      params.append('limit', limit.toString());
    }
    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const url = params.toString()
      ? `${this.baseUrl}/glowpick/products?${params.toString()}`
      : `${this.baseUrl}/glowpick/products`;

    console.info('🌐 API Request:', url);
    const response = await axios.get<GlowpickSearchResponse>(url);

    // Handle both array and object responses for backward compatibility
    if (Array.isArray(response.data)) {
      const products = response.data;
      // Use actual product count instead of artificial multiplier
      const actualTotal = products.length;
      console.info(
        `🌐 API response with glowpick/products?${params.toString()} ${products.length}/${actualTotal} products received`,
      );
      return { products, total: actualTotal };
    } else {
      const data = response.data;
      const products = data.products || [];
      console.info(
        `🌐 API response with glowpick/products?${params.toString()} ${products.length}/${data.total || 'unknown'} products received`,
      );
      return data;
    }
  }

  // Get products by range with pagination info
  async getProductsByRange(range: string, limit?: number, lastEvaluatedKey?: string): Promise<GlowpickSearchResponse> {
    const params = new URLSearchParams();
    params.append('range', range);

    if (limit) {
      params.append('limit', limit.toString());
    }
    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const url = `${this.baseUrl}/glowpick/products?${params.toString()}`;
    console.info('🌐 API Request:', url);
    const response = await axios.get<GlowpickSearchResponse>(url);

    // Handle both array and object responses for backward compatibility
    if (Array.isArray(response.data)) {
      const products = response.data;
      // Use actual product count instead of artificial multiplier
      const actualTotal = products.length;
      console.info(
        `🌐 API response with glowpick/products?${params.toString()} ${products.length}/${actualTotal} products received`,
      );
      return { products, total: actualTotal };
    } else {
      const data = response.data;
      const products = data.products || [];
      console.info(
        `🌐 API response with glowpick/products?${params.toString()} ${products.length}/${data.total || 'unknown'} products received`,
      );
      return data;
    }
  }

  // Get products by brand with pagination info
  async getProductsByBrand(brand: string, limit?: number, lastEvaluatedKey?: string): Promise<GlowpickSearchResponse> {
    const params = new URLSearchParams();
    params.append('brand', brand);

    if (limit) {
      params.append('limit', limit.toString());
    }
    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const url = `${this.baseUrl}/glowpick/products?${params.toString()}`;
    console.info('🌐 API Request:', url);
    const response = await axios.get<GlowpickSearchResponse>(url);

    // Handle both array and object responses for backward compatibility
    if (Array.isArray(response.data)) {
      const products = response.data;
      // Use actual product count instead of artificial multiplier
      const actualTotal = products.length;
      console.info(
        `🌐 API response with glowpick/products?${params.toString()} ${products.length}/${actualTotal} products received`,
      );
      return { products, total: actualTotal };
    } else {
      const data = response.data;
      const products = data.products || [];
      console.info(
        `🌐 API response with glowpick/products?${params.toString()} ${products.length}/${data.total || 'unknown'} products received`,
      );
      return data;
    }
  }

  // Get products by category with pagination info
  async getProductsByCategory(
    category: string,
    subcategory?: string,
    limit?: number,
    lastEvaluatedKey?: string,
  ): Promise<GlowpickSearchResponse> {
    console.info('🔍 getProductsByCategory called with:', { category, subcategory, limit });
    console.info('🔍 Raw parameters before encoding:', { category, subcategory });
    console.info('🔍 Category length:', category?.length, 'Subcategory length:', subcategory?.length);
    console.info('🔍 Category empty?', !category, 'Subcategory empty?', !subcategory);

    const params = new URLSearchParams();
    params.append('category', category);
    if (subcategory) {
      params.append('subcategory', subcategory);
      console.info('🔍 Added subcategory to params:', subcategory);
    } else {
      console.info('🔍 No subcategory provided, skipping subcategory parameter');
    }

    if (limit) {
      params.append('limit', limit.toString());
    }
    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const paramString = params.toString();
    const url = `${this.baseUrl}/glowpick/products?${paramString}`;
    console.info('🌐 API Request URL:', url);
    console.info('🔍 URL parameters string:', paramString);
    console.info('🔍 Encoded category parameter:', encodeURIComponent(category));
    if (subcategory) {
      console.info('🔍 Encoded subcategory parameter:', encodeURIComponent(subcategory));
    }

    const response = await axios.get<GlowpickSearchResponse>(url);

    // Handle both array and object responses for backward compatibility
    if (Array.isArray(response.data)) {
      const products = response.data;
      // Use actual product count instead of artificial multiplier
      const actualTotal = products.length;
      console.info(
        `🌐 API response with glowpick/products?${params.toString()} ${products.length}/${actualTotal} products received`,
      );
      return { products, total: actualTotal };
    } else {
      const data = response.data;
      const products = data.products || [];
      console.info(
        `🌐 API response with glowpick/products?${params.toString()} ${products.length}/${data.total || 'unknown'} products received`,
      );
      return data;
    }
  }

  // Get products by award period with pagination info
  async getProductsByAward(
    awardPeriod: string,
    limit?: number,
    lastEvaluatedKey?: string,
  ): Promise<GlowpickSearchResponse> {
    const params = new URLSearchParams();
    params.append('awardPeriod', awardPeriod);

    if (limit) {
      params.append('limit', limit.toString());
    }
    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const url = `${this.baseUrl}/glowpick/products?${params.toString()}`;
    console.info(`🌐 API Request: ${url}`);
    const response = await axios.get<GlowpickSearchResponse>(url);

    // Handle both array and object responses for backward compatibility
    if (Array.isArray(response.data)) {
      const products = response.data;
      // Use actual product count instead of artificial multiplier
      const actualTotal = products.length;
      console.info(
        `🌐 API Response with glowpick/products?${params.toString()}: ${products.length}/${actualTotal} products received`,
      );
      return { products, total: actualTotal };
    } else {
      const data = response.data;
      const products = data.products || [];
      console.info(
        `🌐 API Response with glowpick/products?${params.toString()}: ${products.length}/${data.total || 'unknown'} products received`,
      );
      return data;
    }
  }

  // Get all brands
  async getAllBrands(): Promise<string[]> {
    const url = `${this.baseUrl}/glowpick/metadata/brand`;
    console.info('🌐 API Request:', url);
    const response = await axios.get<MetadataResponse>(url);
    const brands = response.data.metadata?.brands || [];
    console.info(`🌐 API response with metadata/brand ${brands.length} brands received`);
    return brands;
  }

  // Get all categories
  async getAllCategories(): Promise<{ categories: string[]; subcategories: Record<string, string[]> }> {
    const url = `${this.baseUrl}/glowpick/metadata/category`;
    console.info('🌐 API Request:', url);
    const response = await axios.get<MetadataResponse>(url);
    const categories = response.data.metadata?.categories || [];
    const subcategories = response.data.metadata?.subcategories || {};
    console.info(
      `🌐 API response with glowpick/metadata/category ${categories.length} categories, ${Object.keys(subcategories).length} subcategory groups received`,
    );
    console.info('🔍 Available categories:', categories);
    console.info(
      '🔍 Category containing "스킨케어":',
      categories.find((cat) => cat.includes('스킨케어')),
    );
    console.info(
      '🔍 All categories with Korean characters:',
      categories.filter((cat) => /[가-힣]/.test(cat)),
    );
    return { categories, subcategories };
  }

  // Get product by ID
  async getProductById(productId: string): Promise<GlowpickProduct> {
    const url = `${this.baseUrl}/glowpick/products/${productId}`;
    console.info('🌐 API Request:', url);
    const response = await axios.get(url);
    console.info(`🌐 API response with glowpick/products/:productId Single product received`);
    return response.data;
  }

  // Get last product ID metadata
  async getLastProductId(): Promise<{ lastProductId: string; lastUpdated: string } | null> {
    try {
      const url = `${this.baseUrl}/glowpick/metadata/lastProductId`;
      console.info('🌐 API Request:', url);
      const response = await axios.get<LastProductIdResponse>(url);
      const data = response.data;
      console.info(
        `🌐 API response with glowpick/metadata/lastProductId ${data ? `Last product ID: ${data.metadata?.lastProductId}` : 'No data received'}`,
        data ? `Last product ID: ${data.metadata?.lastProductId}` : 'No data received',
      );

      if (data?.metadata?.lastProductId && data?.updatedAt) {
        return {
          lastProductId: data.metadata.lastProductId.toString(),
          lastUpdated: data.updatedAt,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching last product ID:', error);
      return null;
    }
  }

  // Update product (including scAgentEngData)
  async updateProduct(
    productId: string,
    updates: Record<string, any>,
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const url = `${this.baseUrl}/glowpick/products/${productId}`;
      console.info('🌐 API Request (PATCH):', url);
      console.info('📊 Updates:', updates);

      await axios.patch(url, updates, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.info(`✅ Product updated successfully for product ${productId}`);
      return { success: true };
    } catch (error) {
      console.error(`❌ Failed to update product ${productId}:`, error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async deleteCpProductsFromGlowpickMetadata(
    gpProductId: string,
    cpUniqueId: string,
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const gpProduct = await this.getProductById(gpProductId);
      if (!gpProduct?.metadata?.cpProducts) {
        console.info(`⚠️ No cpProducts metadata found for GP product: ${gpProductId}`);
        return { success: true };
      }

      console.info(`📝 Updating Glowpick metadata for GP product: ${gpProductId}`);
      const updatedCpProducts = gpProduct.metadata.cpProducts.filter((cp) => cp.cpUniqueId !== cpUniqueId);

      const updatedMetadata = {
        cpProducts: updatedCpProducts,
        notFoundInCoupang: false,
      };

      await this.updateProductMetadata(gpProductId, updatedMetadata);
      console.info(`✅ Successfully updated Glowpick metadata for GP product: ${gpProductId}`);
      return { success: true };
    } catch (error) {
      console.error(`❌ Failed to delete cpProduct from Glowpick metadata for product ${gpProductId}:`, error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Update product metadata
  async updateProductMetadata(
    productId: string,
    metadata: Record<string, any>,
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const url = `${this.baseUrl}/glowpick/products/${productId}`;
      console.info('🌐 API Request (PATCH):', url);
      console.info('📊 Metadata to update:', metadata);

      await axios.patch(
        url,
        { metadata },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      console.info(`✅ Product metadata updated successfully for product ${productId}`);
      return { success: true };
    } catch (error) {
      console.error(`❌ Failed to update product metadata for product ${productId}:`, error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Clear product metadata (for re-scraping)
  async clearProductMetadata(productId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const url = `${this.baseUrl}/glowpick/products/${productId}`;
      console.info('🌐 API Request (PATCH) - Clearing metadata:', url);

      await axios.patch(
        url,
        { metadata: null },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      console.info(`✅ Product metadata cleared successfully for product ${productId}`);
      return { success: true };
    } catch (error) {
      console.error(`❌ Failed to clear product metadata for product ${productId}:`, error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

export const glowpickClient = new GlowpickClient();
